import {
  batchCreateBasicField,
  batchCreateEvent,
  batchCreateEventType,
  batchCreateIntermediarySuperForField,
  batchEditEventType,
  batchMovePersonToOtherRecord,
  createFieldValue,
  createHighlightsDispatches,
  createStandardizedFieldValue,
  getFloatingElementById,
  getNestedElementById,
  getNestedOrFloatingElementById,
  getNextUuid,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'
import { FamilyReviewApp } from '../../../../shared/services/config'
import saveSls from '../../../utils/taskSaveUtils'
import { getFieldIdAndValue } from '../../indexing/components/editPanels/utils/editPanelDispatchBuilders'
import { generateDeletePersonDispatches } from '../../../../shared/utils/utils'

const attribution = { comment: '', reasonId: INDEXED_INCORRECTLY, app: FamilyReviewApp }

const savePrimaryPerson = async ({
  primaryPerson,
  currentTask,
  partitionKey,
  zionSLSState,
  zionSlsDispatch,
  userId,
  saveChangesFlag,
}) => {
  // create dispatches
  let dispatches = [
    {
      type: 'SET_PRIMARY',
      value: {
        partitionKey,
        id: primaryPerson.id,
        primary: true,
      },
    },
  ]
  if (primaryPerson.recordId !== currentTask.id) {
    const fromRecord = getNestedOrFloatingElementById(zionSLSState.images, primaryPerson.recordId)
    dispatches = dispatches.concat(
      batchMovePersonToOtherRecord({
        person: primaryPerson,
        destRecordId: currentTask.recordId,
        sourceRecPartitionKey: fromRecord.partitionKey,
        destRecPartitionKey: currentTask.partitionKey,
      })
    )
  }
  // save to sls and update state
  await saveSls({
    dispatches,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
  })
}

async function saveNotPrimaryPerson({
  notPrimaries,
  partitionKey,
  zionSLSState,
  zionSlsDispatch,
  userId,
  saveChangesFlag,
}) {
  // create dispatches
  const dispatches = notPrimaries.flatMap((personId) => [
    {
      type: 'SET_PRIMARY',
      value: {
        partitionKey,
        id: personId,
        primary: false,
      },
    },
  ])
  // save to sls and update state
  await saveSls({
    dispatches,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
  })
}

async function saveAttributionToRecord({ currentTask, userId, zionSLSState, zionSlsDispatch, saveChangesFlag }) {
  const recordId = currentTask.id
  const partitionKey = currentTask.partitionKey
  const dispatches = [
    {
      type: 'SET_REVIEW_ATTRIBUTION',
      value: {
        partitionKey,
        id: recordId,
        elementType: 'RECORD',
        sequenceNumber: 1,
        attributionArg: {},
      },
    },
  ]
  await saveSls({
    dispatches,
    updateState: false,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
  })
}

async function saveNewEvent({
  eventId,
  highlights,
  isPrimary = false,
  partitionKey,
  personId,
  saveChangesFlag,
  type,
  typeFieldId,
  userId,
  recordId,
  zionSLSState,
  zionSlsDispatch,
}) {
  const images = zionSLSState.images
  const eventTypeLabel = type?.displayLabel
  const eventTypeFieldValue = createFieldValue(eventTypeLabel, attribution, type?.uri)

  // create event dispatches (person and type of event)
  let dispatches = batchCreateEvent({
    partitionKey,
    eventType: type?.eventType,
    personId,
    id: eventId,
    eventTypeId: typeFieldId,
    eventTypeFieldValue,
    extraArgs: { primary: isPrimary },
  })
  const highlightDispatches = createHighlightsDispatches(images, partitionKey, highlights, '', true)
  dispatches = dispatches.concat(highlightDispatches)
  if (dispatches.length) dispatches.push({ type: 'SET_RECORD_PERSON_IDS', value: recordId })

  // save to sls and update state
  if (dispatches)
    await saveSls({
      dispatches,
      userId,
      taskTypeParam: 'Family Review',
      appName: FamilyReviewApp,
      saveChangesFlag,
      zionSLSState,
      zionSlsDispatch,
    })
}

async function saveFloatingEventToPerson({
  eventId,
  personId,
  saveChangesFlag,
  userId,
  zionSLSState,
  zionSlsDispatch,
}) {
  const personElement = getNestedElementById(zionSLSState.images, personId)
  const floatingEventElement = getFloatingElementById(zionSLSState.images, eventId)
  // create event dispatches (person and type of event)
  if (personElement && floatingEventElement) {
    const dispatches = [
      {
        type: 'ADD_SUB',
        value: {
          superPartitionKey: personElement.partitionKey,
          subPartitionKey: floatingEventElement.partitionKey,
          superId: personId,
          subId: eventId,
          elementType: 'PERSON',
        },
      },
    ]
    // save to sls and update state
    if (dispatches)
      await saveSls({
        dispatches,
        userId,
        taskTypeParam: 'Family Review',
        appName: FamilyReviewApp,
        saveChangesFlag,
        zionSLSState,
        zionSlsDispatch,
      })
  }
}

/**
 * Form values use the following 3 distinct patterns:
 *
 * fieldId: fieldValue (in the case of a basic field)
 * `${fieldId}-fieldType`: fieldType
 * NOTE: The key: `${fieldId}}-fieldType` is custom form logic, needed to specify fieldType on event field creation.
 *
 * {eventId}: { key: eventType , primaryText } (in the case of the mandatory eventType)
 *
 * person-{autosuggest_uuid}: { key: personId, primaryText } (in the case of person autosuggest)
 *      NOTE: Because these autosuggests are added and removed, there is a certain custom submission logic
 *      to determine which persons actually need to be added/removed to/from the event's superElements.
 * */
const saveFamilyReviewEditEvent = async ({
  eventId,
  dirtyFields,
  highlights,
  partitionKey,
  values,
  zionSLSState,
  userId,
  saveChangesFlag,
  zionSlsDispatch,
  unlinkingStates,
  isPrimaryEvent,
  primaryPerson,
  currentTask,
  recordId,
  handleError,
  handleSuccess,
}) => {
  //  __    __   _______  __      .______    _______ .______          _______.
  // |  |  |  | |   ____||  |     |   _  \  |   ____||   _  \        /       |
  // |  |__|  | |  |__   |  |     |  |_)  | |  |__   |  |_)  |      |   (----`
  // |   __   | |   __|  |  |     |   ___/  |   __|  |      /        \   \
  // |  |  |  | |  |____ |  `----.|  |      |  |____ |  |\  \----.----)   |
  //  __|  |__| |_______||_______|| _|      |_______|| _| `._____|_______/

  const getEventTypeFieldFromEvent = (eventElement) => {
    if (eventElement?.subElements) {
      return eventElement.subElements.find((el) => el.fieldType === 'EVENT_TYPE')
    }
    return null
  }
  const appendRemoveElementDispatch = ({ dispatches, element }) => {
    dispatches.push({
      type: 'REMOVE_ELEMENT',
      value: {
        id: element.id,
        partitionKey: element.partitionKey,
        elementType: element.elementType,
      },
    })
    return dispatches
  }
  const appendDirtyFieldDispatches = ({ dispatches, el, value }) => {
    if (value === 'REMOVED' || value === '') {
      dispatches = appendRemoveElementDispatch({ dispatches, element: el })
    } else {
      const { id: fieldId, value: fieldValue } = getFieldIdAndValue({ el, value, attribution })
      dispatches.push({ type: 'ADD_FV', value: { partitionKey: el.partitionKey, id: fieldId, fieldValue } })
    }

    return dispatches
  }
  const appendAddPersonDispatches = ({ dispatches, personId }) => {
    dispatches.push({
      type: 'ADD_SUB',
      value: {
        superPartitionKey: partitionKey,
        subPartitionKey: partitionKey,
        superId: personId,
        subId: eventId,
        elementType: 'PERSON',
      },
    })
    return dispatches
  }
  const appendRemovePersonDispatches = ({ dispatches, personId }) => {
    dispatches.push({
      type: 'REM_SUB',
      value: {
        superPartitionKey: partitionKey,
        subPartitionKey: partitionKey,
        superId: personId,
        subId: eventId,
        elementType: 'PERSON',
      },
    })
    return dispatches
  }
  const appendNewEventTypeDispatches = ({ dispatches, eventElement, value }) => {
    // Create a new eventType field
    const eventTypeFieldId = getNextUuid()
    dispatches = [
      ...dispatches,
      ...batchCreateEventType({
        partitionKey: eventElement.partitionKey,
        typeFieldId: eventTypeFieldId,
        typeFieldValue: createFieldValue(value.primaryText, { ...attribution, reasonId: '' }),
        elementId: eventElement.id,
      }),
    ]

    return dispatches
  }
  const appendPreExistingEventTypeDispatches = ({ dispatches, typeFieldId, value }) => {
    dispatches = [
      ...dispatches,
      ...batchEditEventType({
        partitionKey,
        typeFieldId,
        typeFieldValue: createFieldValue(value.primaryText, attribution, value.uri),
      }),
    ]

    return dispatches
  }
  const appendAnyEventTypeDispatches = ({ dispatches, el, value }) => {
    // Remember, we're dealing with the event field here, that's what el is
    const typeFieldId = getEventTypeFieldFromEvent(el)?.id || null
    const eventTypeFieldIsDirty = dirtyFields[eventId]
    if (!typeFieldId) {
      dispatches = appendNewEventTypeDispatches({ dispatches, eventElement: el, value })
    } else if (eventTypeFieldIsDirty) {
      dispatches = appendPreExistingEventTypeDispatches({
        dispatches,
        typeFieldId,
        value,
      })
    }
    // possible fallthrough for a non-dirty pre-existing event type

    return dispatches
  }
  const appendNewFieldDispatches = ({ dispatches, key, value, superId, superType, fieldType }) => {
    let fieldValue = createFieldValue(typeof value === 'string' ? value : value.originalText, {
      ...attribution,
      reasonId: '',
    })
    if (value.standard) {
      fieldValue = createStandardizedFieldValue(value, fieldType, attribution)
    }
    dispatches = [
      ...dispatches,
      ...batchCreateBasicField({
        partitionKey,
        superId,
        fieldType,
        fieldValue,
        superElementType: superType,
        id: key,
      }),
    ]
    return dispatches
  }
  const newDateElementDispatches = ({ dispatches, dateElementId }) => {
    return [
      ...dispatches,
      ...batchCreateIntermediarySuperForField({
        partitionKey,
        intermediaryType: 'DATE',
        superId: eventId,
        intermediaryId: dateElementId,
        superType: 'EVENT',
      }),
    ]
  }
  const newPlaceElementDispatches = ({ dispatches, placeElementId }) => {
    return [
      ...dispatches,
      ...batchCreateIntermediarySuperForField({
        partitionKey,
        intermediaryType: 'PLACE',
        superId: eventId,
        intermediaryId: placeElementId,
        superType: 'EVENT',
      }),
    ]
  }

  const appendUnlinkDispatches = ({
    dispatches,
    el,
    value,
    key,
    dateElementId,
    placeElementId,
    updatedHighlightsParam,
  }) => {
    const fieldSuperType = el?.typeData?.elementTypes?.[0]
    let updatedHighlights = updatedHighlightsParam
    let superId = eventId
    if (fieldSuperType === 'DATE') {
      superId = dateElementId
    }
    if (fieldSuperType === 'PLACE') {
      superId = placeElementId
    }
    // Unlink shared fields. Each dispatch will be of type 'REM_SUB' and will have the superId as the eventId and the subId as the unlinking state id
    dispatches.push({
      type: 'REM_SUB',
      value: {
        superPartitionKey: partitionKey,
        subPartitionKey: partitionKey,
        superId,
        subId: key,
        elementType: fieldSuperType,
      },
    })
    const newFieldId = getNextUuid()
    dispatches = dispatches.concat(
      batchCreateBasicField({
        partitionKey,
        superId,
        fieldType: el.fieldType,
        fieldValue: createFieldValue(value, attribution),
        superElementType: fieldSuperType,
        id: newFieldId,
      })
    )
    updatedHighlights = updatedHighlights.map((h) => {
      if (h.fieldId === el.id) {
        // Save new highlight changes to the new field
        return {
          ...h,
          fieldId: newFieldId,
        }
      }
      if (h.change === 'deselected' && h.originalHighlight.fieldId === el.id) {
        return {
          ...h,
          fieldId: h.originalHighlight.fieldId,
          change: null,
        }
      }
      return h
    })
    return { dispatches, updatedHighlights }
  }

  const appendFormDispatches = ({ dispatches, valuesArray, event, updatedHighlightsParam }) => {
    // These elements may need to be created, and their ID's remembered for further addition of fields, thus the "let" declaration
    let dateElementId = event.subElements.find((el) => el.elementType === 'DATE')?.id || ''
    let placeElementId = event.subElements.find((el) => el.elementType === 'PLACE')?.id || ''
    let updatedHighlights = updatedHighlightsParam
    valuesArray
      .filter(
        ([key, value]) =>
          (!key.includes('-fieldType') && // This is a helper form field to specify the fieldType of a new subelement to Event
            !key.includes('-superType') && // This is a helper form field to specify the superElementType (Date, Place, or Event) of a new subelement to Event
            !key.includes('person') && // Taken care of a different way
            (typeof value === 'string' || value.originalText || value.standard)) || // Make sure Date and Place fields are defined.
          (!key.includes('person') && value.key && value.primaryText !== '') // For the EventType field
      )
      .forEach(([key, value], index) => {
        const el = getNestedOrFloatingElementById(zionSLSState.images, key)
        const slsElementExistsOnImage = Boolean(el)
        const keyValueIsDirty = dirtyFields[key]

        // These describe what SLS element id the current form key represents
        const isEventElementId = slsElementExistsOnImage && el.elementType === 'EVENT'
        const isFieldElementId = el?.elementType === 'FIELD'
        const isNewFieldElement = !slsElementExistsOnImage

        if (isEventElementId) {
          dispatches = appendAnyEventTypeDispatches({ index, dispatches, el, value, dirtyFields })
        } else if (isFieldElementId) {
          if (unlinkingStates[key]?.state) {
            const result = appendUnlinkDispatches({
              dispatches,
              el,
              value,
              key,
              dateElementId,
              placeElementId,
              updatedHighlightsParam: updatedHighlights,
            })
            dispatches = result.dispatches
            updatedHighlights = result.updatedHighlights
          } else {
            if (!keyValueIsDirty) return
            dispatches = appendDirtyFieldDispatches({ index, dispatches, el, value })
          }
        } else if (isNewFieldElement) {
          if (!value) return // Empty input value from the form, we won't worry about it

          const fieldSuperType = valuesArray.find(([k]) => k === `${key}-superType`)[1]
          const fieldType = valuesArray.find(([k]) => k === `${key}-fieldType`)[1]
          if (fieldSuperType === 'DATE' && !dateElementId) {
            dateElementId = getNextUuid()
            dispatches = newDateElementDispatches({ dispatches, dateElementId })
          } else if (fieldSuperType === 'PLACE' && !placeElementId) {
            placeElementId = getNextUuid()
            dispatches = newPlaceElementDispatches({ dispatches, placeElementId })
          }
          dispatches = appendNewFieldDispatches({
            dispatches,
            valuesArray,
            fieldType,
            key,
            value,
            superType: fieldSuperType,
            superId: fieldSuperType === 'DATE' ? dateElementId : placeElementId,
          })
        }
      })

    return { dispatches, updatedHighlights }
  }

  const appendPersonDispatches = ({ dispatches, valuesArray, event }) => {
    // person exists as a form value in one of the autosuggests, but not as a super element to the event, so add ADD_SUB dispatches
    const personIdsToBeAdded = valuesArray
      .filter(([key, value]) => key.includes('person') && value && value.key !== '')
      .filter(([, { key: personId }]) => {
        return event.superElements.every((se) => se.id !== personId)
      })
      .map(([, { key: personId }]) => personId)
    personIdsToBeAdded.forEach((personId) => {
      dispatches = appendAddPersonDispatches({ personId, eventId, dispatches })
    })

    // person exists as a super element to the event, but not as a form value in the autosuggest, and isn't the primary person, who is handled below, so add REM_SUB dispatches
    let personIdsToBeRemoved = event.superElements
      .filter(({ id }) => {
        return valuesArray.every(([key, value]) => {
          // Guard clause, don't care about these pairs
          if (!key.includes('person') || !value || !value.key) return true
          // Person id isn't in any of the person's autosuggest form values
          return id !== value.key
        })
      })
      .filter(({ id }) => {
        const isOnCurrentTask = Boolean(getNestedElementById([currentTask], id))
        return isOnCurrentTask
      })
      .map(({ id }) => id)
    if (isPrimaryEvent) personIdsToBeRemoved = personIdsToBeRemoved?.filter((id) => id !== primaryPerson.id)

    personIdsToBeRemoved.forEach((personId) => {
      dispatches = appendRemovePersonDispatches({ personId, eventId, dispatches })
    })

    // Add the primary person to the primary event
    if (isPrimaryEvent) {
      // If it's not already there
      if (event.superElements.every((se) => se.id !== primaryPerson.id)) {
        dispatches = appendAddPersonDispatches({ personId: primaryPerson.id, eventId, dispatches })
      }
    }

    return dispatches
  }

  // .______     ______    _______  ____    ____
  // |   _  \   /  __  \  |       \ \   \  /   /
  // |  |_)  | |  |  |  | |  .--.  | \   \/   /
  // |   _  <  |  |  |  | |  |  |  |  \_    _/
  // |  |_)  | |  `--'  | |  '--'  |    |  |
  // |______/   \______/  |_______/     |__|

  let dispatches = []
  let updatedHighlights = highlights
  const event = getNestedOrFloatingElementById([zionSLSState.images[0]], eventId)

  const valuesArray = Object.entries(values)

  dispatches = appendPersonDispatches({ valuesArray, event, dispatches, isPrimaryEvent })

  const result = appendFormDispatches({ valuesArray, dispatches, event, updatedHighlightsParam: updatedHighlights })
  dispatches = result.dispatches
  updatedHighlights = result.updatedHighlights
  dispatches = [
    ...dispatches,
    ...createHighlightsDispatches(zionSLSState.images, partitionKey, updatedHighlights, '', true),
  ]

  if (dispatches.length) {
    dispatches.push({ type: 'SET_RECORD_PERSON_IDS', value: recordId })
    await saveSls({
      dispatches,
      userId,
      taskTypeParam: 'Family Review',
      appName: FamilyReviewApp,
      saveChangesFlag,
      zionSLSState,
      zionSlsDispatch,
      handleError,
      handleSuccess,
    })
  } else {
    handleSuccess()
  }
}

async function removePersonFromRecord({
  partitionKey,
  recordId,
  personId,
  zionSLSState,
  zionSlsDispatch,
  userId,
  saveChangesFlag,
}) {
  const dispatches = [
    {
      type: 'REM_SUB',
      value: {
        superId: recordId,
        superPartitionKey: partitionKey,
        subId: personId,
        subPartitionKey: partitionKey,
        elementType: 'RECORD',
      },
    },
  ]
  await saveSls({
    dispatches,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
  })
}

async function removeEventFromPerson({
  partitionKey,
  personIds,
  eventId,
  zionSLSState,
  zionSlsDispatch,
  userId,
  saveChangesFlag,
}) {
  const dispatches = []
  personIds.forEach((personId) => {
    dispatches.push({
      type: 'REM_SUB',
      value: {
        superId: personId.id,
        superPartitionKey: partitionKey,
        subId: eventId,
        subPartitionKey: partitionKey,
        elementType: 'PERSON',
      },
    })
  })
  await saveSls({
    dispatches,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
  })
}
async function deletePerson({ partitionKey, personId, zionSLSState, zionSlsDispatch, userId, saveChangesFlag }) {
  const dispatches = generateDeletePersonDispatches(personId, partitionKey, zionSLSState.images)
  await saveSls({
    dispatches,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
  })
}

export {
  deletePerson,
  savePrimaryPerson,
  saveNotPrimaryPerson,
  saveAttributionToRecord,
  saveNewEvent,
  saveFamilyReviewEditEvent,
  removePersonFromRecord,
  saveFloatingEventToPerson,
  removeEventFromPerson,
}
